import { isThemeConfigured, readThemeConfiguration } from './themeConfiguration';

/**
 * Résout un objet couleur `{ label, style }` ou une chaîne en valeur CSS.
 */
function resolveColorValue(colorObj) {
  if (!colorObj) return null;
  if (typeof colorObj === 'string') return colorObj === 'transparent' ? 'transparent' : colorObj;
  return colorObj.style || null;
}

/**
 * Extrait les propriétés visuelles d'un style de titre.
 */
function extractTitleStyle(titleConfig) {
  if (!titleConfig) return null;
  return {
    // Le Proposal lit `title.fontFamily`, vide = « celle du document »
    // (TitleConfiguration.jsx:199). Elle n'était pas extraite : côté site, tous
    // les niveaux de titre retombaient sur la police de base, quoi qu'on
    // configure.
    fontFamily: titleConfig.fontFamily || null,
    fontSize: titleConfig.fontSize || null,
    color: resolveColorValue(titleConfig.color),
    // Graisse vide = normale, comme dans le classique (titleUtils) : sans
    // règle, le navigateur mettrait le <hN> en gras.
    fontWeight: titleConfig.fontWeight || 'normal',
    fontStyle: titleConfig.fontStyle || null,
    textDecoration: titleConfig.textDecoration || null,
    marginTop: titleConfig.marginTop ?? 0,
    marginBottom: titleConfig.marginBottom ?? 0,
    backgroundColor: resolveColorValue(titleConfig.backgroundColor),
    textAlign: titleConfig.textAlign || null,
    // Réglages du site seul (le classique ne les lit pas) : un grand titre
    // d'affiche se serre, ce que l'interligne du navigateur ne fait pas.
    lineHeight: titleConfig.lineHeight || null,
    letterSpacing: titleConfig.letterSpacing || null,
  };
}

/**
 * Extrait les styles globaux de la configuration du proposal.
 * Retourne un objet `proposalStyles` à stocker dans `projectConfig`.
 */
export function extractProposalStyles(configuration = {}) {
  return {
    fontFamily: configuration.fontFamily || null,
    fontFamilyList: (configuration.fontFamilyList || []).map((f) => f?.familyName).filter(Boolean),
    fontSize: configuration.fontSize || null,
    baseFontColor: resolveColorValue(configuration.baseFontColor),
    lineSpacing: configuration.lineSpacing || null,
    title1: extractTitleStyle(configuration.title1),
    title2: extractTitleStyle(configuration.title2),
    title3: extractTitleStyle(configuration.title3),
    title4: extractTitleStyle(configuration.title4),
  };
}

/**
 * Les styles de texte effectifs d'un site généré : un thème configuré gagne
 * (cf. chantier-theme.md) ; sinon, ceux du document.
 */
export function siteTextStyles(projectConfig) {
  const theme = projectConfig?.theme;
  if (isThemeConfigured(theme)) return extractProposalStyles(readThemeConfiguration(theme));
  return projectConfig?.proposalStyles || null;
}

/**
 * La police d'un bloc de texte généré, thème compris. Le parser fige dans
 * chaque bloc la police de son style de texte — le plus souvent celle du
 * document. Cette police-là cède devant celle du thème, comme les polices par
 * défaut du bloc (`blockDefaults`, celles d'un bloc déposé) ; une police
 * choisie exprès reste.
 */
export function themedTextFontFamily(fontFamily, projectConfig, blockDefaults = []) {
  const theme = projectConfig?.theme;
  if (!fontFamily || !isThemeConfigured(theme)) return fontFamily;
  const themeFont = readThemeConfiguration(theme).fontFamily;
  const documentFont = projectConfig?.proposalStyles?.fontFamily;
  if (!themeFont) return fontFamily;
  return fontFamily === documentFont || blockDefaults.includes(fontFamily) ? themeFont : fontFamily;
}

// Valeur du bloc, valeur par défaut du document qu'elle peut recopier.
const INHERITABLE_TEXT_VALUES = [
  ['fontSize', 'fontSize'],
  ['color', 'baseFontColor'],
];

/**
 * Le style d'un bloc de texte généré, thème compris : comme la police
 * (`themedTextFontFamily`), la taille et la couleur figées depuis le défaut
 * du document cèdent devant celles du thème. Une valeur forcée reste.
 */
export function themedTextStyle(textStyle, projectConfig) {
  const theme = projectConfig?.theme;
  if (!textStyle || !isThemeConfigured(theme)) return textStyle;
  const themeStyles = extractProposalStyles(readThemeConfiguration(theme));
  const documentStyles = projectConfig?.proposalStyles || {};
  const themed = { ...textStyle, fontFamily: themedTextFontFamily(textStyle.fontFamily, projectConfig) };
  INHERITABLE_TEXT_VALUES.forEach(([key, defaultKey]) => {
    const value = textStyle[key];
    if (value == null || String(value) !== String(documentStyles[defaultKey])) return;
    themed[key] = themeStyles[defaultKey] ?? value;
  });
  return themed;
}
